// Blog data hook — fetches published posts.
// Production images do not run the Payload CMS Next.js child (no
// Node.js in the base image), so the frontend reads directly from
// FastAPI's `/api/blog/posts` endpoint, which queries MongoDB. On
// preview the same endpoint continues to work — it just happens to
// coexist with the CMS proxy path (CMS is used only for admin edits).
import { useEffect, useState } from "react";

const BASE = "/api/blog";

// Payload stores absolute media URLs baked with the production host
// (e.g. https://mts.enterprises/api/cms/api/media/file/xyz.png).
// On preview / any non-prod host, browsers cannot resolve that origin,
// so we strip everything up to and including `/api/cms/api/media/` and
// rebuild the URL against the CURRENT origin. Same-origin URLs and
// simple relative paths pass through untouched.
function _rewriteMediaUrl(u) {
  if (!u || typeof u !== "string") return u;
  // Absolute URL with a foreign host → keep only the `/api/cms/...` path.
  const m = u.match(/^https?:\/\/[^/]+(\/api\/cms\/.+)$/i);
  if (m) return m[1];
  return u;
}

function _walkAndRewriteMedia(node) {
  if (node == null || typeof node !== "object") return node;
  if (Array.isArray(node)) {
    for (let i = 0; i < node.length; i += 1) node[i] = _walkAndRewriteMedia(node[i]);
    return node;
  }
  // Payload media docs always carry `url` + `mimeType` + `filename`.
  if (typeof node.url === "string" && (node.mimeType || node.filename || node.sizes)) {
    node.url = _rewriteMediaUrl(node.url);
    if (node.thumbnailURL) node.thumbnailURL = _rewriteMediaUrl(node.thumbnailURL);
    if (node.sizes && typeof node.sizes === "object") {
      for (const k of Object.keys(node.sizes)) {
        const s = node.sizes[k];
        if (s && typeof s === "object" && typeof s.url === "string") {
          s.url = _rewriteMediaUrl(s.url);
        }
      }
    }
  }
  for (const k of Object.keys(node)) {
    node[k] = _walkAndRewriteMedia(node[k]);
  }
  return node;
}

export async function fetchPublishedList({ page = 1, limit = 15, search = "", category = "", vertical = "" } = {}) {
  const params = new URLSearchParams();
  params.set("depth", "1");
  params.set("limit", String(limit));
  params.set("page", String(page));
  params.set("sort", "-publishedAt");
  if (search) params.set("search", search);
  if (category) params.set("category", category);
  if (vertical) params.set("vertical", vertical);
  const r = await fetch(`${BASE}/posts?${params}`);
  if (!r.ok) throw new Error(`Blog list failed: HTTP ${r.status}`);
  return _walkAndRewriteMedia(await r.json());
}

export async function fetchPublishedBySlug(slug) {
  const params = new URLSearchParams();
  params.set("depth", "2");
  const r = await fetch(`${BASE}/posts/${encodeURIComponent(slug)}?${params}`);
  if (r.status === 404) return null;
  if (!r.ok) throw new Error(`Blog article failed: HTTP ${r.status}`);
  return _walkAndRewriteMedia(await r.json());
}

export function useBlogList(query) {
  const [state, setState] = useState({ loading: true, error: null, data: null });
  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, error: null, data: null });
    fetchPublishedList(query)
      .then((data) => !cancelled && setState({ loading: false, error: null, data }))
      .catch((e) => !cancelled && setState({ loading: false, error: e.message, data: null }));
    return () => { cancelled = true; };
  }, [query.page, query.limit, query.search, query.category, query.tag]);  // eslint-disable-line react-hooks/exhaustive-deps
  return state;
}

export function useBlogArticle(slug) {
  const [state, setState] = useState({ loading: true, error: null, notFound: false, data: null });
  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    setState({ loading: true, error: null, notFound: false, data: null });

    // Fetch with one automatic retry on transient errors (network drop, 5xx,
    // timeout). A confirmed 404 does NOT retry. This guards the prerenderer
    // (e.g. Emergent's crawler-cache) against snapshotting a Not Found state
    // during a transient hiccup and caching noindex for bots.
    const attempt = (retriesLeft) =>
      fetchPublishedBySlug(slug)
        .then((data) => {
          if (cancelled) return;
          if (data === null) {
            // API explicitly said 404 → article does not exist. Confirmed.
            setState({ loading: false, error: null, notFound: true, data: null });
          } else {
            setState({ loading: false, error: null, notFound: false, data });
          }
        })
        .catch((e) => {
          if (cancelled) return;
          if (retriesLeft > 0) {
            setTimeout(() => attempt(retriesLeft - 1), 500);
            return;
          }
          // Transient failure after retry — surface as `error`, NOT `notFound`.
          // Consumers must distinguish these two cases when emitting `noindex`.
          setState({ loading: false, error: e.message, notFound: false, data: null });
        });

    attempt(1);
    return () => { cancelled = true; };
  }, [slug]);
  return state;
}

// Related articles endpoint (Phase E step 3 — Smart Internal Linking).
// Backend endpoint will be at /api/seo/related/:postId. Falls back to empty.
export async function fetchRelated(postId, { limit = 4 } = {}) {
  try {
    const r = await fetch(`/api/seo/related/${postId}?limit=${limit}`);
    if (!r.ok) return [];
    const body = await r.json();
    return body.related || [];
  } catch {
    return [];
  }
}

// Related services — filtered by article's vertical + category.
export async function fetchRelatedServices(postId, { limit = 3 } = {}) {
  try {
    const r = await fetch(`/api/seo/related/${postId}/services?limit=${limit}`);
    if (!r.ok) return [];
    const body = await r.json();
    return body.services || [];
  } catch {
    return [];
  }
}
